import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  useTheme,
  Menu,
  MenuItem,
} from '@mui/material';
import { ArrowBack, ArrowDropDown } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import CustomStepper from '../components/DataInsertion/CustomStepper';
import AggregatedDataTable from '../components/DataInsertion/AggregatedDataTable';
import DisaggegatedDataTable from '../components/DataInsertion/DisaggregatedDataTable';
import AggregatedGrid from '../components/DataInsertion/AggregatedGrid';
import DisaggregatedGrid from '../components/DataInsertion/DisaggregatedGrid';
import { setLocalStorageItem, getLocalStorageItem } from '../utils/localStorage';

const DataInsertionPageCombined = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const userId = keycloak.tokenParsed?.user_id;

  // Available countries from MapChart
  const availableCountries = ['Greece', 'Italy', 'Denmark', 'Austria', 'Ireland', 'Spain', 'Romania'];

  // State management for selected values
  const [selectedCountry, setSelectedCountry] = useState(
    location.state?.selectedCountry || getLocalStorageItem('selectedCountry') || 'Greece'
  );
  const [selectedOption, setSelectedOption] = useState(
    location.state?.selectedOption || getLocalStorageItem('selectedOption') || 'Aggregated'
  );

  const [activeStep, setActiveStep] = useState(0);
  const [showInsertionPage, setShowInsertionPage] = useState(false);

  // State for dropdown menus
  const [countryMenuAnchor, setCountryMenuAnchor] = useState(null);
  const isCountryMenuOpen = Boolean(countryMenuAnchor);

  // Handlers for country selection
  const handleCountryClick = (event) => {
    setCountryMenuAnchor(event.currentTarget);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setLocalStorageItem('selectedCountry', country);
    setCountryMenuAnchor(null);
  };

  const handleCountryMenuClose = () => {
    setCountryMenuAnchor(null);
  };

  // Handler for option toggle
  const handleOptionToggle = () => {
    const newOption = selectedOption === 'Aggregated' ? 'Disaggregated' : 'Aggregated';
    setSelectedOption(newOption);
    setLocalStorageItem('selectedOption', newOption);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleBackButton = () => {
    if (showInsertionPage) {
      setShowInsertionPage(false);
    } else {
      navigate(-1);
    }
  };

  const handleShowInsertionPage = () => {
    setShowInsertionPage(true);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {!showInsertionPage ? (
        <Container
          maxWidth="lg"
          sx={{
            paddingBottom: '80px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: '100vh',
          }}
        >
          {/* Page Title and Selected Info */}
          <Box sx={{ marginTop: 4, marginBottom: 0 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Data Insertion
            </Typography>

            {/* Display selected country and option as clickable boxes */}
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 1, gap: 2 }}>
              {/* Country Selection Box */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ marginRight: 1 }}>
                  Selected Country
                </Typography>
                <Box
                  component="button"
                  onClick={handleCountryClick}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      borderColor: theme.palette.primary.main,
                    },
                    '&:focus': {
                      outline: 'none',
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
                    },
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 'bold', marginRight: 0.5 }}>
                    {selectedCountry}
                  </Typography>
                  <ArrowDropDown fontSize="small" />
                </Box>

                {/* Country Dropdown Menu */}
                <Menu
                  anchorEl={countryMenuAnchor}
                  open={isCountryMenuOpen}
                  onClose={handleCountryMenuClose}
                  MenuListProps={{
                    'aria-labelledby': 'country-button',
                  }}
                  PaperProps={{
                    sx: {
                      maxHeight: 300,
                      minWidth: 150,
                    },
                  }}
                >
                  {availableCountries.map((country) => (
                    <MenuItem
                      key={country}
                      onClick={() => handleCountrySelect(country)}
                      selected={country === selectedCountry}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.main + '20',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.main + '30',
                          },
                        },
                      }}
                    >
                      {country}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>

              {/* Option Toggle Box */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ marginRight: 1 }}>
                  Selected Option
                </Typography>
                <Box
                  component="button"
                  onClick={handleOptionToggle}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      borderColor: theme.palette.secondary.main,
                    },
                    '&:focus': {
                      outline: 'none',
                      borderColor: theme.palette.secondary.main,
                      boxShadow: `0 0 0 2px ${theme.palette.secondary.main}25`,
                    },
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 'bold', marginRight: 0.5 }}>
                    {selectedOption}
                  </Typography>
                  <ArrowDropDown fontSize="small" />
                </Box>
              </Box>
            </Box>
          </Box>

          {selectedOption === 'Aggregated' && (
            <>
              <AggregatedGrid />

              {/* Data Table and Button Layout */}
              <Grid container spacing={2} alignItems="flex-start">
                {/* Aggregated Data Table */}
                <Grid item xs={12} md={9}>
                  <AggregatedDataTable />
                </Grid>

                {/* Add Data Button */}
                <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, marginTop: { xs: 10, md: 8 } }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      color: 'white',
                      backgroundColor: theme.palette.primary.main,
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                      width: '100%',
                      height: '60px',
                      fontSize: '1.2rem',
                      borderRadius: '8px',
                      maxWidth: '240px',
                      '&:hover': {
                        // backgroundColor: theme.palette.action.hover, // Light gray on hover
                        backgroundColor: theme.palette.secondary.main, // Changed to primary color on hover
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                    onClick={handleShowInsertionPage}
                  >
                    + Click to add Data
                  </Button>
                </Grid>
              </Grid>

              {/* If you need to include the warning and other elements, ensure they are placed appropriately */}
              <Box
                sx={{
                  marginTop: 3,
                }}
              >
                {/* Additional content if needed */}
              </Box>
            </>
          )}

          {selectedOption === 'Disaggregated' && (
            <>
              <DisaggregatedGrid />
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={9}>
                  <DisaggegatedDataTable />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, marginTop: { xs: 10, md: 8 } }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      color: 'white',
                      backgroundColor: theme.palette.primary.main,
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                      width: '100%',
                      height: '60px',
                      fontSize: '1.2rem',
                      borderRadius: '8px',
                      maxWidth: '240px',
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.main,
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                    onClick={handleShowInsertionPage}
                  >
                    + Click to add Data
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
        </Container>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              marginBottom: 3,
            }}
            onClick={handleBackButton}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ArrowBack sx={{ marginLeft: 1, color: 'gray' }} />
              <Typography variant="h6" sx={{ fontStyle: 'italic', color: 'gray' }}>
                Return to Data Sample page
              </Typography>
            </Box>
          </Box>
          <CustomStepper
            activeStep={activeStep}
            handleNext={handleNext}
            handleBack={handleBack}
            handleBackButton={handleBackButton}
            selectedCountry={selectedCountry}
            selectedOption={selectedOption}
            selectedUserId={userId}
          />
        </Box>
      )}
    </Box>
  );
};

export default DataInsertionPageCombined;